<template>
  <div class="user-settings-page">
    <!-- ── Page Header ── -->
    <div class="ms-page-header">
      <div class="ms-page-header__content">
        <div class="ms-page-header__icon">
          <i class="fa-solid fa-user-gear"></i>
        </div>
        <div class="flex-grow-1">
          <p class="ms-page-header__breadcrumb">
            Platform Settings <span class="ms-breadcrumb-sep">/</span> User Settings
          </p>
          <h1 class="ms-page-header__title">Spark Platform User Settings</h1>
          <p class="ms-page-header__subtitle">
            Manage your personal Spark platform preferences and third-party integration credentials (such as your personal Atlassian API token)
          </p>
        </div>
        <div>
          <router-link to="/platform-bug-reporting" class="ms-btn ms-btn--outline">
            <i class="fa-solid fa-bug"></i> Go to Bug Reporting
          </router-link>
        </div>
      </div>
    </div>

    <!-- ── Summary Cards ── -->
    <div class="ms-summary-row">
      <div class="ms-stat-card">
        <div class="ms-stat-icon" style="background: linear-gradient(135deg, #00A0C1, #33b5d1);">
          <i class="fa-solid fa-id-badge"></i>
        </div>
        <div class="ms-stat-body">
          <span class="ms-stat-value">{{ userFullName || 'Spark User' }}</span>
          <span class="ms-stat-label">{{ userRoleLabel || 'Platform Member' }} · {{ userEmail || 'No email' }}</span>
        </div>
      </div>

      <div class="ms-stat-card">
        <div class="ms-stat-icon" style="background: linear-gradient(135deg, #f59e0b, #fbbf24);">
          <i class="fa-solid fa-building"></i>
        </div>
        <div class="ms-stat-body">
          <span class="ms-stat-value">{{ organisationName }}</span>
          <span class="ms-stat-label">Active Organisation</span>
        </div>
      </div>

      <div class="ms-stat-card">
        <div
          class="ms-stat-icon"
          :style="
            userJiraCred.has_personal_token
              ? 'background: linear-gradient(135deg, #10b981, #34d399);'
              : 'background: linear-gradient(135deg, #64748b, #94a3b8);'
          "
        >
          <i class="fa-solid fa-key"></i>
        </div>
        <div class="ms-stat-body">
          <span class="ms-stat-value">
            {{ userJiraCred.has_personal_token ? 'Atlassian Token Active' : 'General Fallback' }}
          </span>
          <span class="ms-stat-label">
            {{
              userJiraCred.has_personal_token
                ? `Connected as ${userJiraCred.jira_display_name || userJiraCred.jira_email}`
                : 'Manual Jira pushes use general token until configured'
            }}
          </span>
        </div>
      </div>
    </div>

    <!-- ── Main Content Grid ── -->
    <div class="us-grid">
      <!-- Left Column: User Profile & Workflow Guide -->
      <div class="us-col-left">
        <!-- Spark Profile Card -->
        <div class="ms-card mb-4">
          <div class="ms-card__header">
            <div class="d-flex align-items-center gap-2">
              <i class="fa-solid fa-circle-user text-info"></i>
              <h2 class="ms-card__title mb-0">Logged-In Spark Identity</h2>
            </div>
            <span class="us-role-pill" v-if="userRoleLabel">{{ userRoleLabel }}</span>
          </div>

          <div class="ms-card__body">
            <div class="us-profile-hero">
              <div class="us-avatar">
                {{ userInitials }}
              </div>
              <div class="us-profile-info">
                <div class="us-profile-name">{{ userFullName }}</div>
                <div class="us-profile-email">
                  <i class="fa-solid fa-envelope me-1"></i> {{ userEmail || 'Not available' }}
                </div>
                <div class="us-profile-org mt-1" v-if="organisationName">
                  <i class="fa-solid fa-building me-1"></i> {{ organisationName }}
                </div>
              </div>
            </div>

            <div class="us-info-list mt-3">
              <div class="us-info-row">
                <span class="us-info-label">Spark User ID</span>
                <span class="us-info-val font-monospace">#{{ userId || '—' }}</span>
              </div>
              <div class="us-info-row">
                <span class="us-info-label">Authentication Provider</span>
                <span class="us-info-val">Vertex Central Auth (SSO)</span>
              </div>
              <div class="us-info-row">
                <span class="us-info-label">Atlassian / Jira Identity</span>
                <span class="us-info-val">
                  <span v-if="userJiraCred.has_personal_token" class="text-success fw-bold">
                    <i class="fa-solid fa-circle-check me-1"></i>
                    {{ userJiraCred.jira_display_name || userJiraCred.jira_email }} (Personal)
                  </span>
                  <span v-else class="text-secondary">
                    <i class="fa-solid fa-robot me-1"></i> General Platform Token
                  </span>
                </span>
              </div>
            </div>

            <div class="mt-3 pt-3 border-top d-flex align-items-center justify-content-between flex-wrap gap-2">
              <span class="text-muted" style="font-size: 12px;">
                Need to update your name, password, or 2FA?
              </span>
              <button type="button" class="ms-btn ms-btn--primary" @click="openExternalVertexAuth">
                <i class="fa-solid fa-arrow-up-right-from-square"></i> Vertex Auth Account
              </button>
            </div>
          </div>
        </div>

        <!-- How Atlassian Token Resolution Works Card -->
        <div class="ms-card">
          <div class="ms-card__header">
            <div class="d-flex align-items-center gap-2">
              <i class="fa-solid fa-route text-primary"></i>
              <h2 class="ms-card__title mb-0">How Atlassian Token Resolution Works</h2>
            </div>
          </div>
          <div class="ms-card__body">
            <div class="us-rule-item">
              <div class="us-rule-icon bg-primary-subtle text-primary">
                <i class="fa-solid fa-hand-pointer"></i>
              </div>
              <div>
                <div class="us-rule-title">1. Manual "Push to Jira" (My Atlassian Token)</div>
                <p class="us-rule-desc mb-0">
                  When you manually click <strong>Push to Jira</strong> or <strong>Re-Push</strong> on a bug report, Spark prioritizes your personal Atlassian API token configured on this page so the Jira ticket is created under your own Atlassian account.
                </p>
              </div>
            </div>

            <div class="us-rule-item mt-3">
              <div class="us-rule-icon bg-info-subtle text-info">
                <i class="fa-solid fa-robot"></i>
              </div>
              <div>
                <div class="us-rule-title">2. Automated Sync &amp; Bitbucket Repos (General Token)</div>
                <p class="us-rule-desc mb-0">
                  When a platform has <strong>Auto-Sync New Bug Reports to Jira</strong> enabled or reads Bitbucket repositories on the <code>dev</code> branch, Spark uses the platform's general Atlassian token in <code>.env</code>.
                </p>
              </div>
            </div>

            <div class="us-rule-item mt-3">
              <div class="us-rule-icon bg-warning-subtle text-warning">
                <i class="fa-solid fa-rotate"></i>
              </div>
              <div>
                <div class="us-rule-title">3. Deleted Jira Ticket Resolution</div>
                <p class="us-rule-desc mb-0">
                  If an issue is deleted in Jira, clicking <strong>Re-Push</strong> in Bug Reporting automatically creates a fresh replacement Jira ticket and updates the report link.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: My Atlassian Token Configuration -->
      <div class="us-col-right">
        <div class="ms-card">
          <div class="ms-card__header">
            <div class="d-flex align-items-center gap-2">
              <i class="fa-solid fa-key text-info" style="font-size: 1.15rem;"></i>
              <div>
                <h2 class="ms-card__title mb-0">My Atlassian Token</h2>
                <p class="text-muted mb-0" style="font-size: 12px;">
                  Link your personal Atlassian API token to push bug reports under your own Jira / Atlassian identity
                </p>
              </div>
            </div>
            <span
              class="us-status-badge"
              :class="userJiraCred.has_personal_token ? 'us-status-badge--active' : 'us-status-badge--inactive'"
            >
              <i
                class="fa-solid me-1"
                :class="userJiraCred.has_personal_token ? 'fa-circle-check' : 'fa-circle-info'"
              ></i>
              {{ userJiraCred.has_personal_token ? 'Verified & Active' : 'Not Configured' }}
            </span>
          </div>

          <div class="ms-card__body">
            <!-- Active Connection Banner -->
            <div v-if="userJiraCred.has_personal_token" class="us-connected-banner mb-4">
              <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div class="d-flex align-items-center gap-3">
                  <div class="us-connected-icon">
                    <i class="fa-solid fa-shield-halved"></i>
                  </div>
                  <div>
                    <div class="fw-bold text-success" style="font-size: 14px;">
                      Connected to {{ userJiraCred.jira_domain || 'vertex.atlassian.net' }}
                    </div>
                    <div class="text-muted" style="font-size: 12.5px;">
                      Account: <strong>{{ userJiraCred.jira_display_name || userJiraCred.jira_email }}</strong>
                      ({{ userJiraCred.jira_email }})
                    </div>
                  </div>
                </div>
                <div class="text-end">
                  <span class="badge bg-white text-dark border font-monospace px-2 py-1">
                    Token: {{ userJiraCred.masked_token || '••••••••' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Feedback Alert -->
            <div
              v-if="feedbackMsg"
              class="alert py-2 px-3 mb-3 d-flex align-items-center justify-content-between"
              :class="feedbackType === 'success' ? 'alert-success' : 'alert-danger'"
              style="font-size: 13px; border-radius: 10px;"
            >
              <span>
                <i
                  class="fa-solid me-2"
                  :class="feedbackType === 'success' ? 'fa-circle-check' : 'fa-triangle-exclamation'"
                ></i>
                {{ feedbackMsg }}
              </span>
              <button type="button" class="btn-close" style="font-size: 10px;" @click="feedbackMsg = ''"></button>
            </div>

            <!-- Configuration Form -->
            <form @submit.prevent="saveUserJiraCredential">
              <div class="mb-3">
                <label class="us-label">Atlassian / Jira Cloud Domain</label>
                <div class="input-group">
                  <span class="input-group-text bg-light text-muted">https://</span>
                  <input
                    type="text"
                    class="form-control"
                    v-model="userJiraCred.jira_domain"
                    placeholder="vertex.atlassian.net"
                    :disabled="userJiraCred.has_personal_token"
                  />
                </div>
                <small class="text-muted" style="font-size: 11.5px;">
                  Your organisation's Atlassian Cloud hostname (defaults to <code>vertex.atlassian.net</code>).
                </small>
              </div>

              <div class="mb-3">
                <label class="us-label">Your Atlassian Account Email <span class="text-danger">*</span></label>
                <input
                  type="email"
                  class="form-control"
                  v-model="userJiraCred.jira_email"
                  placeholder="e.g. name@vtxsecurity.com"
                  :disabled="userJiraCred.has_personal_token"
                  required
                />
                <small class="text-muted" style="font-size: 11.5px;">
                  Defaults to your logged-in Spark email (<strong>{{ userEmail || 'your account' }}</strong>).
                </small>
              </div>

              <div v-if="!userJiraCred.has_personal_token" class="mb-4">
                <div class="d-flex align-items-center justify-content-between mb-1">
                  <label class="us-label mb-0">
                    Your Personal Atlassian API Token <span class="text-danger">*</span>
                  </label>
                  <a
                    href="https://id.atlassian.com/manage-profile/security/api-tokens"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-primary text-decoration-none fw-semibold"
                    style="font-size: 12px;"
                  >
                    <i class="fa-solid fa-arrow-up-right-from-square me-1"></i>
                    Generate token at id.atlassian.com
                  </a>
                </div>
                <div class="input-group">
                  <input
                    :type="showToken ? 'text' : 'password'"
                    class="form-control font-monospace"
                    v-model="userJiraCred.jira_api_token"
                    placeholder="Paste your Atlassian API token (ATATT3xFfGF0...)"
                  />
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="showToken = !showToken"
                    :title="showToken ? 'Hide token' : 'Show token'"
                  >
                    <i class="fa-solid" :class="showToken ? 'fa-eye-slash' : 'fa-eye'"></i>
                  </button>
                </div>
                <small class="text-muted" style="font-size: 11.5px;">
                  Spark verifies your Atlassian token live against <code>/rest/api/3/myself</code> before saving.
                </small>
              </div>

              <div class="d-flex align-items-center justify-content-end pt-3 border-top flex-wrap gap-2">
                <button
                  v-if="userJiraCred.has_personal_token"
                  type="button"
                  class="btn btn-outline-danger"
                  style="border-radius: 10px; padding: 8px 16px; font-size: 0.85rem; font-weight: 600;"
                  :disabled="userJiraCred.isSaving"
                  @click="deleteUserJiraCredential"
                >
                  <i class="fa-solid fa-trash-can me-1"></i> Remove Atlassian Token
                </button>
                <button
                  v-else
                  type="submit"
                  class="ms-btn ms-btn--primary"
                  :disabled="userJiraCred.isSaving"
                >
                  <i
                    class="fa-solid"
                    :class="userJiraCred.isSaving ? 'fa-spinner fa-spin' : 'fa-plug-circle-check'"
                  ></i>
                  {{ userJiraCred.isSaving ? 'Verifying with Atlassian...' : 'Verify & Save Atlassian Token' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SparkUserSettingsDashboard",
  data() {
    return {
      showToken: false,
      autoSeededOnce: false,
      feedbackMsg: "",
      feedbackType: "success",
      userJiraCred: {
        has_personal_token: false,
        jira_domain: "vertex.atlassian.net",
        jira_email: "",
        jira_api_token: "",
        jira_display_name: "",
        masked_token: "",
        isSaving: false,
      },
    };
  },
  computed: {
    currentUser() {
      return this.$store?.state?.auth?.currentUser || {};
    },
    userId() {
      return this.currentUser.id || 0;
    },
    userEmail() {
      return this.currentUser.email || "";
    },
    userFullName() {
      const first = this.currentUser.first_name || "";
      const last = this.currentUser.last_name || "";
      return `${first} ${last}`.trim() || "Spark User";
    },
    userInitials() {
      const first = this.currentUser.first_name ? this.currentUser.first_name.charAt(0) : "S";
      const last = this.currentUser.last_name ? this.currentUser.last_name.charAt(0) : "U";
      return (first + last).toUpperCase();
    },
    userRoleLabel() {
      if (this.$store?.state?.auth?.isSuperAdmin) return "Super Admin";
      if (this.$store?.state?.auth?.isAdmin) return "Admin";
      if (this.$store?.state?.auth?.isConsultant) return "Consultant";
      return "User";
    },
    organisationName() {
      return this.currentUser?.organisation?.name || "Vertex Cyber Security";
    },
  },
  mounted() {
    this.fetchUserJiraCredential();
    this.onJiraCredUpdated = () => {
      this.fetchUserJiraCredential();
    };
    window.addEventListener("spark-user-jira-cred-updated", this.onJiraCredUpdated);
  },
  beforeUnmount() {
    if (this.onJiraCredUpdated) {
      window.removeEventListener("spark-user-jira-cred-updated", this.onJiraCredUpdated);
    }
  },
  methods: {
    openExternalVertexAuth() {
      window.open(this.$config.getAuthBaseUrl() + "/user-settings/", "_blank");
    },

    fetchUserJiraCredential() {
      const params = {};
      if (this.userId) params.user_id = this.userId;
      if (this.userEmail) params.user_email = this.userEmail;

      this.$axios
        .get("/api/platform-bug-reporting/user-jira-credentials", { params })
        .then((res) => {
          if (res.data && res.data.success) {
            const d = res.data.data || res.data;
            this.userJiraCred.has_personal_token = !!d.has_personal_token;
            this.userJiraCred.jira_domain = d.jira_domain || "vertex.atlassian.net";
            this.userJiraCred.jira_email = d.jira_email || this.userEmail || "";
            this.userJiraCred.jira_display_name = d.jira_display_name || "";
            this.userJiraCred.masked_token = d.masked_token || "";
            this.userJiraCred.jira_api_token = "";
          }
        })
        .catch(() => {
          if (!this.userJiraCred.jira_email && this.userEmail) {
            this.userJiraCred.jira_email = this.userEmail;
          }
        });
    },

    saveUserJiraCredential() {
      this.feedbackMsg = "";
      if (!this.userJiraCred.jira_email) {
        this.feedbackType = "error";
        this.feedbackMsg = "Please enter your Atlassian account email.";
        return;
      }
      if (!this.userJiraCred.jira_api_token) {
        this.feedbackType = "error";
        this.feedbackMsg = "Please paste your personal Jira API token to verify and save.";
        return;
      }

      this.userJiraCred.isSaving = true;
      const payload = {
        user_id: this.userId || 0,
        user_email: this.userEmail || this.userJiraCred.jira_email,
        jira_domain: (this.userJiraCred.jira_domain || "vertex.atlassian.net").trim(),
        jira_email: this.userJiraCred.jira_email.trim(),
        jira_api_token: this.userJiraCred.jira_api_token.trim(),
      };

      this.$axios
        .post("/api/platform-bug-reporting/user-jira-credentials", payload)
        .then((res) => {
          if (res.data && res.data.success) {
            const d = res.data.data || res.data;
            this.userJiraCred.has_personal_token = true;
            this.userJiraCred.jira_domain = d.jira_domain || payload.jira_domain;
            this.userJiraCred.jira_email = d.jira_email || payload.jira_email;
            this.userJiraCred.jira_display_name = d.jira_display_name || "";
            this.userJiraCred.masked_token = d.masked_token || "••••••••";
            this.userJiraCred.jira_api_token = "";
            this.feedbackType = "success";
            this.feedbackMsg =
              res.data.message ||
              `Verified with Jira as ${this.userJiraCred.jira_display_name || this.userJiraCred.jira_email}!`;
            window.dispatchEvent(
              new CustomEvent("spark-user-jira-cred-updated", {
                detail: { ...this.userJiraCred },
              })
            );
          } else {
            this.feedbackType = "error";
            this.feedbackMsg = (res.data && res.data.message) || "Failed to verify Jira token.";
          }
        })
        .catch((err) => {
          this.feedbackType = "error";
          this.feedbackMsg =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to verify Jira credentials. Please check your email and API token.";
        })
        .finally(() => {
          this.userJiraCred.isSaving = false;
        });
    },

    deleteUserJiraCredential() {
      this.userJiraCred.isSaving = true;
      this.feedbackMsg = "";
      const params = {};
      if (this.userId) params.user_id = this.userId;
      if (this.userEmail || this.userJiraCred.jira_email) {
        params.user_email = this.userEmail || this.userJiraCred.jira_email;
      }

      this.$axios
        .delete("/api/platform-bug-reporting/user-jira-credentials", { params })
        .then((res) => {
          if (res.data && res.data.success) {
            this.userJiraCred.has_personal_token = false;
            this.userJiraCred.jira_display_name = "";
            this.userJiraCred.masked_token = "";
            this.userJiraCred.jira_api_token = "";
            this.feedbackType = "success";
            this.feedbackMsg = "Personal Jira token removed. Manual pushes will use the general token.";
            window.dispatchEvent(
              new CustomEvent("spark-user-jira-cred-updated", {
                detail: { ...this.userJiraCred },
              })
            );
          }
        })
        .catch(() => {
          this.feedbackType = "error";
          this.feedbackMsg = "Failed to remove personal Jira token.";
        })
        .finally(() => {
          this.userJiraCred.isSaving = false;
        });
    },
  },
};
</script>

<style scoped>
.user-settings-page {
  padding: 24px 28px 48px;
  max-width: 1440px;
  margin: 0 auto;
}

/* ── Page Header ── */
.ms-page-header {
  margin-bottom: 24px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
  border: 1px solid rgba(0, 130, 152, 0.15);
  border-left: 5px solid #008298;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(0, 78, 90, 0.05);
}

.ms-page-header__content {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.ms-page-header__icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(0, 130, 152, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: #008298;
  flex-shrink: 0;
}

.ms-page-header__title {
  font-size: 1.45rem;
  font-weight: 800;
  color: #00363f;
  margin: 0;
  letter-spacing: -0.3px;
}

.ms-page-header__subtitle {
  font-size: 0.85rem;
  color: #557d84;
  margin: 4px 0 0;
}

.ms-page-header__breadcrumb {
  font-size: 0.76rem;
  color: #557d84;
  font-weight: 600;
  margin: 0 0 4px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.ms-breadcrumb-sep {
  margin: 0 6px;
  color: rgba(0, 130, 152, 0.35);
}

/* ── Summary Row ── */
.ms-summary-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

@media (max-width: 992px) {
  .ms-summary-row {
    grid-template-columns: 1fr;
  }
}

.ms-stat-card {
  background: #fff;
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
}

.ms-stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.ms-stat-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ms-stat-value {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ms-stat-label {
  font-size: 0.78rem;
  color: #64748b;
  font-weight: 500;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Grid Layout ── */
.us-grid {
  display: grid;
  grid-template-columns: 5fr 7fr;
  gap: 24px;
  align-items: start;
}

@media (max-width: 992px) {
  .us-grid {
    grid-template-columns: 1fr;
  }
}

/* ── Card ── */
.ms-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  border: 1px solid #eef2f6;
  overflow: hidden;
}

.ms-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafcfd;
}

.ms-card__title {
  font-size: 1.02rem;
  font-weight: 700;
  color: #0f172a;
}

.ms-card__body {
  padding: 22px;
}

/* ── Profile Summary ── */
.us-profile-hero {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f0f9ff 100%);
  border: 1px solid #e0f2fe;
  border-radius: 12px;
}

.us-avatar {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: linear-gradient(135deg, #008298 0%, #00a0c1 100%);
  color: #fff;
  font-weight: 700;
  font-size: 1.15rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 10px rgba(0, 130, 152, 0.25);
}

.us-profile-name {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
}

.us-profile-email,
.us-profile-org {
  font-size: 0.82rem;
  color: #475569;
}

.us-role-pill {
  background: rgba(0, 160, 193, 0.12);
  color: #008298;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 4px 10px;
  border-radius: 999px;
}

.us-info-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.us-info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.84rem;
  padding: 6px 0;
  border-bottom: 1px dashed #e2e8f0;
}

.us-info-row:last-child {
  border-bottom: none;
}

.us-info-label {
  color: #64748b;
  font-weight: 500;
}

.us-info-val {
  color: #1e293b;
  font-weight: 600;
}

/* ── Rules ── */
.us-rule-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.us-rule-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  flex-shrink: 0;
}

.us-rule-title {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 2px;
}

.us-rule-desc {
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.45;
}

/* ── Jira Card ── */
.us-status-badge {
  font-size: 0.76rem;
  font-weight: 700;
  padding: 5px 12px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
}

.us-status-badge--active {
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #86efac;
}

.us-status-badge--inactive {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.us-connected-banner {
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border: 1px solid #86efac;
  border-radius: 12px;
  padding: 14px 16px;
}

.us-connected-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #16a34a;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.us-label {
  display: block;
  font-size: 0.84rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}

/* ── Buttons ── */
.ms-btn {
  padding: 9px 18px;
  border-radius: 10px;
  font-size: 0.86rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  border: none;
  text-decoration: none;
}

.ms-btn--primary {
  background: linear-gradient(135deg, #00A0C1, #33b5d1);
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 160, 193, 0.3);
}

.ms-btn--primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #008da8, #00A0C1);
  box-shadow: 0 4px 16px rgba(0, 160, 193, 0.4);
  transform: translateY(-1px);
  color: #fff;
}

.ms-btn--outline {
  background: #fff;
  color: #008298;
  border: 1.5px solid rgba(0, 130, 152, 0.3);
}

.ms-btn--outline:hover {
  background: #f0f9ff;
  border-color: #008298;
  color: #006678;
}
</style>
